import { useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Archive, Copy, Loader2, Pencil, Plus, Search, Upload } from 'lucide-react';
import {
    useClientBehaviors,
    useCreateClientBehavior,
    useUpdateClientBehavior,
    useRetireClientBehavior,
    useSaveClientBehaviorToBank,
    type ClientBehavior,
    type ClientBehaviorStatus,
    type ClientBehaviorWritePayload,
} from '@/api/clientBehaviors';
import {
    useBehaviorBank,
    useCopyBehaviorBankToClient,
    type BehaviorBankEntry,
} from '@/api/behaviorBank';
import { useBehaviorTypes } from '@/api/behaviorTypes';
import { useAuthStore } from '@/stores/auth';
import type { ClientRecord } from '@/api/clients';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';

/*
 * Client Behaviors tab
 *
 * Behaviors live on the client and persist across plans (plans reference them
 * live, no snapshot). Two creation paths:
 *   1. Copy from the practice bank — links source_bank_id so edits can be
 *      pushed back via save-to-bank.
 *   2. Direct create — unlinked custom behavior; promote to bank later with
 *      save-to-bank { createNew: true }.
 *
 * Save-to-bank warns that it does NOT propagate to other clients — the v2
 * model treats each client copy as independent after copy.
 *
 * "Delete" here is a soft-retire to Discontinued. The row stays in the
 * client's history; default list filter hides Discontinued.
 */

const STATUS_FILTERS: Array<{ id: ClientBehaviorStatus | 'All'; label: string }> = [
    { id: 'Active', label: 'Active' },
    { id: 'Resolved', label: 'Resolved' },
    { id: 'All', label: 'All' },
];

export function ClientBehaviorsTab({ client: c }: { client: ClientRecord }) {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_treatment_plans'));
    const [statusFilter, setStatusFilter] = useState<ClientBehaviorStatus | 'All'>('Active');

    const listOpts = statusFilter === 'All'
        ? { clientId: c.id, includeResolved: true }
        : { clientId: c.id, status: statusFilter };
    const { data, isLoading, isError, error } = useClientBehaviors(listOpts);

    const createM = useCreateClientBehavior(c.id);
    const updateM = useUpdateClientBehavior(c.id);
    const retireM = useRetireClientBehavior(c.id);
    const saveM = useSaveClientBehaviorToBank(c.id);

    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState<ClientBehavior | null>(null);
    const [copying, setCopying] = useState(false);
    const [savingToBank, setSavingToBank] = useState<ClientBehavior | null>(null);
    const [confirmRetire, setConfirmRetire] = useState<ClientBehavior | null>(null);

    const { data: types } = useBehaviorTypes();
    const activeTypes = useMemo(
        () => (types ?? [])
            .filter((t) => t.status === 'Active')
            .sort((a, b) => a.name.localeCompare(b.name)),
        [types],
    );

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-bold text-text">Behaviors</h2>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setCopying(true)}>
                            <Copy className="w-3.5 h-3.5" /> Copy from bank
                        </Button>
                        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                            <Plus className="w-3.5 h-3.5" /> Add Behavior
                        </Button>
                    </div>
                )}
            </div>

            <Card className="p-0 overflow-hidden">
                <div className="flex items-center gap-1 p-2 border-b border-border">
                    {STATUS_FILTERS.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => setStatusFilter(s.id)}
                            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${statusFilter === s.id
                                    ? 'bg-surface-muted text-text'
                                    : 'text-text-muted hover:text-text hover:bg-surface-muted'
                                }`}
                            aria-pressed={statusFilter === s.id}
                        >
                            {s.label}
                        </button>
                    ))}
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {(data?.length ?? 0)} {(data?.length ?? 0) === 1 ? 'behavior' : 'behaviors'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load behaviors</p>
                        <p className="text-xs text-text-subtle">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </div>
                ) : !data || data.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>
                            {statusFilter === 'Active'
                                ? 'No active behaviors for this client yet.'
                                : 'No behaviors in this view.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold">Type</th>
                                    <th className="px-3 py-2 font-bold">Function</th>
                                    <th className="px-3 py-2 font-bold">Status</th>
                                    <th className="px-3 py-2 font-bold">Source</th>
                                    {canEdit && <th className="px-3 py-2 font-bold w-36 text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((b) => (
                                    <tr
                                        key={b.id}
                                        className={`border-b border-border last:border-b-0 transition-colors align-top ${b.status === 'Resolved' ? 'opacity-60 hover:bg-surface-muted' : 'hover:bg-surface-muted'
                                            }`}
                                    >
                                        <td className="px-3 py-2 font-medium">
                                            {b.name}
                                            {b.definition && (
                                                <div className="text-xs text-text-subtle font-normal mt-0.5 line-clamp-2">
                                                    {b.definition}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">{b.behaviorType || '—'}</td>
                                        <td className="px-3 py-2 text-text-muted">{b.function || '—'}</td>
                                        <td className="px-3 py-2"><StatusBadge status={b.status} /></td>
                                        <td className="px-3 py-2 text-xs text-text-subtle whitespace-nowrap">
                                            {b.sourceBankId ? 'Linked to bank' : 'Custom'}
                                        </td>
                                        {canEdit && (
                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                {b.status !== 'Resolved' && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            aria-label={`Edit ${b.name}`}
                                                            onClick={() => setEditing(b)}
                                                            className="mr-1"
                                                        >
                                                            <Pencil className="w-3 h-3" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            aria-label={`Save ${b.name} to bank`}
                                                            title="Save to bank"
                                                            onClick={() => setSavingToBank(b)}
                                                            className="mr-1"
                                                        >
                                                            <Upload className="w-3 h-3" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            aria-label={`Mark ${b.name} resolved`}
                                                            title="Mark resolved"
                                                            onClick={() => setConfirmRetire(b)}
                                                        >
                                                            <Archive className="w-3 h-3" />
                                                        </Button>
                                                    </>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <BehaviorFormDialog
                    initial={editing}
                    activeTypes={activeTypes}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {copying && (
                <CopyFromBankDialog
                    clientId={c.id}
                    onClose={() => setCopying(false)}
                />
            )}

            {savingToBank && (
                <SaveToBankDialog
                    behavior={savingToBank}
                    onClose={() => setSavingToBank(null)}
                    onSubmit={async (createNew) => {
                        await saveM.mutateAsync({ id: savingToBank.id, createNew });
                    }}
                />
            )}

            {confirmRetire && (
                <RetireConfirmDialog
                    behavior={confirmRetire}
                    onClose={() => setConfirmRetire(null)}
                    onConfirm={async () => {
                        await retireM.mutateAsync(confirmRetire.id);
                        setConfirmRetire(null);
                    }}
                />
            )}
        </div>
    );
}

// ─── behavior form (create/edit) ────────────────────────────────────────────

function BehaviorFormDialog({
    initial,
    activeTypes,
    onClose,
    onSubmit,
}: {
    initial: ClientBehavior | null;
    activeTypes: { id: string; name: string }[];
    onClose: () => void;
    onSubmit: (p: ClientBehaviorWritePayload) => Promise<void>;
}) {
    const [name, setName] = useState(initial?.name ?? '');
    const [behaviorTypeId, setTypeId] = useState(initial?.behaviorTypeId ?? '');
    const [definition, setDefinition] = useState(initial?.definition ?? '');
    const [topography, setTopography] = useState(initial?.topography ?? '');
    const [fn, setFn] = useState(initial?.function ?? '');
    const [antecedent, setAntecedent] = useState(initial?.antecedent ?? '');
    const [replacement, setReplacement] = useState(initial?.replacement ?? '');
    const [proactive, setProactive] = useState(initial?.proactive ?? '');
    const [reactive, setReactive] = useState(initial?.reactive ?? '');
    const [baseline, setBaseline] = useState(initial?.baseline ?? '');
    const [reductionGoal, setGoal] = useState(initial?.reductionGoal ?? '');
    const [severityIntensity, setSeverity] = useState(initial?.severityIntensity ?? '');
    const [baselineDate, setBaselineDate] = useState(initial?.baselineDate ?? '');
    const [dataCollectionMethod, setDcm] = useState(initial?.dataCollectionMethod ?? '');
    const [status, setStatus] = useState<ClientBehaviorStatus>(initial?.status ?? 'Active');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required'); return; }
        setSaving(true);
        try {
            await onSubmit({
                name: name.trim(),
                behaviorTypeId: behaviorTypeId || null,
                definition: definition.trim(),
                topography: topography.trim(),
                function: fn.trim(),
                antecedent: antecedent.trim(),
                replacement: replacement.trim(),
                proactive: proactive.trim(),
                reactive: reactive.trim(),
                baseline: baseline.trim(),
                reductionGoal: reductionGoal.trim(),
                severityIntensity: severityIntensity.trim(),
                baselineDate: baselineDate || null,
                dataCollectionMethod: dataCollectionMethod.trim(),
                status,
            });
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[min(calc(100vw-2rem),48rem)] max-h-[85vh] flex flex-col" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit Behavior' : 'Add Behavior'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-1 -mx-1">
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Name" required error={err ?? undefined}>
                            {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                        </FormField>
                        <FormField label="Type">
                            {(id) => (
                                <select
                                    id={id}
                                    value={behaviorTypeId ?? ''}
                                    onChange={(e) => setTypeId(e.target.value)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="">— None —</option>
                                    {activeTypes.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                    </div>
                    {initial && (
                        <FormField label="Status">
                            {(id) => (
                                <select
                                    id={id}
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value as ClientBehaviorStatus)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none max-w-[12rem]"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Resolved">Resolved</option>
                                </select>
                            )}
                        </FormField>
                    )}
                    <FormField label="Definition">
                        {(id) => <Textarea id={id} value={definition} onChange={(e) => setDefinition(e.target.value)} rows={2} />}
                    </FormField>
                    <div className="grid grid-cols-3 gap-x-4">
                        <FormField label="Severity / intensity">
                            {(id) => <Input id={id} value={severityIntensity} onChange={(e) => setSeverity(e.target.value)} />}
                        </FormField>
                        <FormField label="Baseline date">
                            {(id) => <Input id={id} type="date" min="1900-01-01" max={toIsoDate(new Date())} value={baselineDate ?? ''} onChange={(e) => setBaselineDate(e.target.value)} />}
                        </FormField>
                        <FormField label="Data collection method">
                            {(id) => <Input id={id} value={dataCollectionMethod} onChange={(e) => setDcm(e.target.value)} />}
                        </FormField>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Topography">
                            {(id) => <Textarea id={id} value={topography} onChange={(e) => setTopography(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Function">
                            {(id) => <Textarea id={id} value={fn} onChange={(e) => setFn(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Antecedent">
                            {(id) => <Textarea id={id} value={antecedent} onChange={(e) => setAntecedent(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Replacement">
                            {(id) => <Textarea id={id} value={replacement} onChange={(e) => setReplacement(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Proactive">
                            {(id) => <Textarea id={id} value={proactive} onChange={(e) => setProactive(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Reactive">
                            {(id) => <Textarea id={id} value={reactive} onChange={(e) => setReactive(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Baseline">
                            {(id) => <Textarea id={id} value={baseline} onChange={(e) => setBaseline(e.target.value)} rows={2} />}
                        </FormField>
                        <FormField label="Reduction goal">
                            {(id) => <Textarea id={id} value={reductionGoal} onChange={(e) => setGoal(e.target.value)} rows={2} />}
                        </FormField>
                    </div>
                    <DialogFooter className="mt-2">
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ─── copy from bank (picker) ────────────────────────────────────────────────

function CopyFromBankDialog({ clientId, onClose }: { clientId: string; onClose: () => void }) {
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const { data, isLoading } = useBehaviorBank({ behaviorTypeId: typeFilter || undefined });
    const { data: types } = useBehaviorTypes();
    const copyM = useCopyBehaviorBankToClient();

    const activeTypes = useMemo(
        () => (types ?? [])
            .filter((t) => t.status === 'Active')
            .sort((a, b) => a.name.localeCompare(b.name)),
        [types],
    );

    const rows = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        if (!q) return data;
        return data.filter((r) => r.name.toLowerCase().includes(q));
    }, [data, search]);

    async function copyEntry(entry: BehaviorBankEntry) {
        try {
            await copyM.mutateAsync({ id: entry.id, clientId });
            onClose();
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = copyM.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to copy behavior';
        return 'Unexpected error';
    })();

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[min(calc(100vw-2rem),48rem)] max-h-[85vh] flex flex-col" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Copy behavior from bank</DialogTitle>
                </DialogHeader>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-subtle" />
                        <Input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search behaviors…"
                            className="pl-8 h-8 py-1"
                        />
                    </div>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="h-8 px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                    >
                        <option value="">All types</option>
                        {activeTypes.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>
                {serverError && <Alert variant="danger" className="mb-3">{serverError}</Alert>}
                <div className="flex-1 overflow-y-auto border border-border rounded-md">
                    {isLoading ? (
                        <div className="p-8 flex items-center justify-center text-text-subtle">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading bank…
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-8 text-center text-text-subtle text-sm">
                            {data && data.length > 0 ? 'No matches.' : 'The behavior bank is empty.'}
                        </div>
                    ) : (
                        <ul>
                            {rows.map((b) => (
                                <li
                                    key={b.id}
                                    className="flex items-start justify-between gap-3 px-3 py-2 border-b border-border last:border-b-0 hover:bg-surface-muted"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-sm text-text">{b.name}</div>
                                        <div className="text-xs text-text-subtle">
                                            {b.behaviorType || '—'}
                                            {b.function && <> · {b.function}</>}
                                        </div>
                                        {b.definition && (
                                            <div className="text-xs text-text-muted mt-0.5 line-clamp-2">{b.definition}</div>
                                        )}
                                    </div>
                                    <Button
                                        variant="primary"
                                        size="xs"
                                        onClick={() => copyEntry(b)}
                                        disabled={copyM.isPending}
                                    >
                                        Copy to client
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Close</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── save to bank ───────────────────────────────────────────────────────────

function SaveToBankDialog({
    behavior,
    onClose,
    onSubmit,
}: {
    behavior: ClientBehavior;
    onClose: () => void;
    onSubmit: (createNew: boolean) => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [canCreateNew, setCanCreateNew] = useState(false);

    const linked = !!behavior.sourceBankId;

    async function submit(createNew: boolean) {
        setErr(null);
        setWorking(true);
        try {
            await onSubmit(createNew);
            onClose();
        } catch (e) {
            if (e instanceof AxiosError) {
                const data = e.response?.data as { error?: string; canCreateNew?: boolean } | undefined;
                setErr(data?.error ?? 'Could not save to bank');
                if (data?.canCreateNew) setCanCreateNew(true);
            } else {
                setErr(extractErrorMessage(e, 'Could not save to bank'));
            }
        } finally {
            setWorking(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Save to bank?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <p className="text-text-muted">
                        {linked
                            ? <>Push the current values of <span className="font-medium text-text">{behavior.name}</span> to the linked bank entry, replacing what's there.</>
                            : <>This behavior isn't linked to the bank yet. Saving will create a new bank entry from the current values and link this behavior to it.</>
                        }
                    </p>
                    {linked && (
                        <Alert variant="warning" className="text-xs">
                            Other clients who already copied this behavior keep their own
                            values — edits here don't flow to them.
                        </Alert>
                    )}
                    {err && (
                        <Alert variant="danger" className="text-xs">
                            {err}
                        </Alert>
                    )}
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    {canCreateNew ? (
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={working}
                            onClick={() => submit(true)}
                        >
                            {working ? 'Saving…' : 'Create new bank entry'}
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={working}
                            onClick={() => submit(!linked)}
                        >
                            {working ? 'Saving…' : linked ? 'Save to bank' : 'Create bank entry'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── retire (soft-delete) ───────────────────────────────────────────────────

function RetireConfirmDialog({
    behavior,
    onClose,
    onConfirm,
}: {
    behavior: ClientBehavior;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Mark behavior as resolved?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Mark <span className="font-medium text-text">{behavior.name}</span> as
                    resolved? The record stays on the client's history. You can bring it
                    back later from the "All" filter.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try {
                                await onConfirm();
                            } catch (e) {
                                setErr(extractErrorMessage(e, 'Could not update status.'));
                            } finally {
                                setWorking(false);
                            }
                        }}
                    >
                        {working ? 'Working…' : 'Mark resolved'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
